import React, { MutableRefObject, useContext, useEffect, useState } from 'react';
import { Field, FieldArray, FormikProps, getIn, FormikErrors, FormikTouched, Formik, Form } from 'formik';
import NumberFormat, { InputAttributes } from 'react-number-format';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AlertColor, Box, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tab, Tabs, TextField, Tooltip, Typography, FormLabel } from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import HistoryIcon from '@mui/icons-material/History';
import { AMERICAN_DATE_FORMAT, API_DOMAIN, GET, POST, PUT } from '../../../../../utility/constants';
import { formatDate, isObjectsEqual, trimOnBlur } from '../../../../../utility/helper';
import ineligibleAdjustmentsSchema from '../../../../../schemas/ineligibleAdjustmentsSchema';
import { IIneligible, IFormikValue, INumberFormatProps, IResponseContent } from '../../../../../interfaces/ineligibleAdjustmentsInterface';
import { IBBPeriod, IClient, ICollateral } from '../../../../../interfaces';
import { IIneligibleSettingsContext, IIneligibleSettingsPermissions } from '../../../../../interfaces/ineligibleSettingInterface';
import { SelectedClientContext } from '../../../../../context/selectedClientContext';
import { IneligibleSettingsContext } from '../../../../../context/ineligibleSettingsContext';
import Toaster from '../../../../toaster';
import ConfirmModal from '../../../../modals/confirm-modal';
import { SkeletonRow, SkeletonTableHead } from '../../../../skeleton';
import HelperTextComponent from '../../../../common/helper-text-component';
import request from '../../../../../service/request';
import requests from '../../../../../service/requests';
import styles from './styles';

dayjs.extend(customParseFormat);

export interface AdjustmentsPanelProps {
  formikRef: MutableRefObject<FormikProps<IFormikValue> | null>;
}

const CurrencyFormat = React.forwardRef<NumberFormat<InputAttributes>, INumberFormatProps>((props, ref) => {
  const { onChange, ...other } = props;
  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => { onChange({ target: { name: other.name, value: values.value } }); }}
      thousandSeparator
      type='tel'
      decimalScale={2}
      fixedDecimalScale={true}
      prefix='$'
    />
  );
});

const TABS: { label: 'Active' | 'Archived' }[] = [{ label: 'Active' }, { label: 'Archived' }];
const HEADERS = ['Date Created', 'Description', 'Ineligible Amount', 'Action'];

const getCollateralFk = (collateralId: number, ineligibleType: string): { arCollateralFk: number } | { invCollateralFk: number } => {
  return ineligibleType === 'AR' ? { arCollateralFk: collateralId } : { invCollateralFk: collateralId };
};

/**
 * Component that renders the Adjustments ineligible setting's panel: the ineligible adjustments ledger
 * (create/edit/delete/archive/restore), scoped to the currently selected client, collateral, and As of
 * Date (selected via the sibling selector rendered in ineligible-rule, shared through
 * IneligibleSettingsContext.selectedAdjustmentsAsOfDate). Rendered as an always-mounted sibling (see
 * ineligible-rule-override) so its Formik state survives switching to a different ineligible and back.
 */
const IneligibleAdjustmentsPanel: React.FC<AdjustmentsPanelProps> = (props) => {
  const { selectedClient }                                     = useContext(SelectedClientContext);
  const {
    selectedCollateral,
    permissions,
    selectedAdjustmentsAsOfDate,
    setIsAdjustmentsDirty,
  }                                                             = useContext(IneligibleSettingsContext) as IIneligibleSettingsContext;

  const ineligibleType                                         = selectedCollateral?.collateralType === 'INV' ? 'Inventory' : 'AR';

  const [ineligibles, setIneligibles]                           = useState<IIneligible[]>([]);
  const [totalAmount, setTotalAmount]                           = useState<number>(0);
  const [isLoading, setIsLoading]                               = useState<boolean>(false);
  const [allowAddingRow, setAllowAddingRow]                     = useState<boolean>(true);
  const [isDirty, setIsDirty]                                   = useState<boolean>(false);
  const [selectedTabIndex, setSelectedTabIndex]                 = useState<number>(TABS.findIndex(tab => tab.label === 'Active'));
  const [tempTabIndex, setTempTabIndex]                         = useState<number>(TABS.findIndex(tab => tab.label === 'Active'));
  const [showPrompt, setShowPrompt]                             = useState<boolean>(false);
  const [isToasterOpen, setIsToasterOpen]                       = useState<boolean>(false);
  const [toasterMessage, setToasterMessage]                     = useState<string>('');
  const [toasterSeverity, setToasterSeverity]                   = useState<AlertColor>('success');
  const [deleteModal, setDeleteModal]                           = useState({ isOpen: false, isNew: false, deleteRecordId: '', deleteName: '', deleteIndex: -1 });
  const [formikValues, setFormikValues]                         = useState<IFormikValue | null>(null);
  const [isFormikResetting, setIsFormikResetting]               = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen]                           = useState<boolean>(false);
  const [modalTitle, setModalTitle]                             = useState<string>('');
  const [modalDescription, setModalDescription]                 = useState<string>('');
  const [modalConfirmText, setModalConfirmText]                 = useState<string>('');
  const [selectedIneligible, setSelectedIneligible]             = useState<IIneligible | null>(null);

  useEffect(() => {
    if (isLoading) { return; }
    !isFormikResetting && setIsFormikResetting(true);
    fetchIneligibles();
  }, [selectedCollateral?.recordId, selectedTabIndex, selectedAdjustmentsAsOfDate?.recordId]);

  useEffect(() => {
    if (formikValues === null || formikValues.ineligibles === undefined) { return; }
    const { savedFormikValues, emptyFormikValues } = getSavedAndEmptyFormikValues(formikValues.ineligibles);
    calcTotalAmount(savedFormikValues);
    emptyFormikValues.length > 0 ? setAllowAddingRow(false) : setAllowAddingRow(true);
  }, [formikValues]);

  const tabProps = (index: number) => ({
    id: `ineligible-adjustments-tab-${index}`,
    'aria-controls': `ineligible-adjustments-tabpanel-${index}`,
  });

  const handleDiscardTabChange = () => {
    setIsDirty(false);
    setIneligibles([]);
    setSelectedTabIndex(tempTabIndex);
    setShowPrompt(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (isDirty) {
      setShowPrompt(true);
      setTempTabIndex(newValue);
      return;
    }
    setIneligibles([]);
    setSelectedTabIndex(newValue);
  };

  const fetchIneligibles = () => {
    if (selectedClient?.recordId === undefined || selectedCollateral?.recordId === undefined || selectedAdjustmentsAsOfDate === null) {
      setIneligibles([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const isArchived = selectedTabIndex === TABS.findIndex(tab => tab.label === 'Archived');
    request({
      url: `${API_DOMAIN}/bb/ineligibleAdjustments/search/findIsArchived?borrowerId=${selectedClient.recordId}&pageNo=0&pageSize=9999&sortBy=recordId,ASC&isArchived=${isArchived}`,
      method: GET,
    })
      .then((response) => {
        const data = response.data.content.filter((item: IResponseContent) => item.isCurrent === true);
        const ineligibleData: IIneligible[] = data
          .filter((row: IResponseContent) => {
            if (row.ineligibleType !== ineligibleType) { return false; }
            if (ineligibleType === 'AR' && row.arCollateralFk !== selectedCollateral.recordId) { return false; }
            if (ineligibleType === 'Inventory' && row.invCollateralFk !== selectedCollateral.recordId) { return false; }
            if (row.bbPeriodFk !== selectedAdjustmentsAsOfDate.recordId) { return false; }
            return true;
          })
          .map((row: IResponseContent) => ({
            recordId: row.recordId.toString(),
            bbPeriodFk: row.bbPeriodFk,
            dateCreated: dayjs(row.appCreatedDate, 'DD/MM/YY').format('MM/DD/YYYY'),
            description: row.description,
            ineligibleAmount: row.amount.toFixed(2),
            canDelete: row.canDelete === 1,
          }));

        setIsDirty(false);
        setIneligibles(ineligibleData);
      })
      .catch((error) => console.log('INELIGIBLE ADJUSTMENTS GET ERROR: ', error))
      .finally(() => setIsLoading(false));
  };

  const deleteIneligible = (recordId: string, isFetchingAfter?: boolean) => {
    const deletedIneligible = ineligibles.filter(ineligible => ineligible.recordId === recordId)[0];
    const deleteConfig = {
      url: `${API_DOMAIN}/bb/ineligibleAdjustments/${deletedIneligible.recordId}`,
      method: PUT,
      data: {
        recordId: parseInt(deletedIneligible.recordId),
        borrowerFk: (selectedClient as IClient).recordId,
        bbPeriodFk: deletedIneligible.bbPeriodFk,
        ...getCollateralFk((selectedCollateral as ICollateral).recordId, ineligibleType),
        description: deletedIneligible.description,
        amount: parseFloat(deletedIneligible.ineligibleAmount),
        appCreatedDate: dayjs(deletedIneligible.dateCreated, 'MM/DD/YYYY').format('DD/MM/YY'),
        ineligibleType,
        isCurrent: false,
      },
    };

    request(deleteConfig)
      .then(() => {
        const itemName = deleteModal.deleteName ? deleteModal.deleteName : 'Item';
        setToasterMessage(`${itemName} has been deleted`);
        setToasterSeverity('success');
        setIsToasterOpen(true);
        if (isFetchingAfter) { fetchIneligibles(); }
      })
      .catch((error) => console.log('INELIGIBLE ADJUSTMENTS DELETE ERROR: ', error));
  };

  const archiveIneligible = () => {
    request({ url: `${API_DOMAIN}/bb/ineligibleAdjustments/${selectedIneligible?.recordId}/archive`, method: PUT })
      .then(() => {
        setToasterMessage(`${selectedIneligible?.description} has been archived.`);
        setToasterSeverity('success');
        setIsToasterOpen(true);
        fetchIneligibles();
      })
      .catch((error) => {
        console.log(error);
        setToasterMessage(`Failed to archive ${selectedIneligible?.description}. Please check your inputs!`);
        setToasterSeverity('error');
        setIsToasterOpen(true);
      });
    closeModal();
  };

  const restoreIneligible = () => {
    request({ url: `${API_DOMAIN}/bb/ineligibleAdjustments/${selectedIneligible?.recordId}/archive?isArchived=false`, method: PUT })
      .then(() => {
        setToasterMessage(`${selectedIneligible?.description} has been restored.`);
        setToasterSeverity('success');
        setIsToasterOpen(true);
        fetchIneligibles();
      })
      .catch((error) => {
        console.log(error);
        setToasterMessage(`Failed to restore ${selectedIneligible?.description}. Please check your inputs!`);
        setToasterSeverity('error');
        setIsToasterOpen(true);
      });
    closeModal();
  };

  const calcTotalAmount = (ineligiblesArr: IIneligible[]) => {
    let total = 0;
    for (const item of ineligiblesArr) { total += parseFloat(item.ineligibleAmount); }
    setTotalAmount(total);
  };

  const handleDelete = (values: IFormikValue, index: number) => {
    const isIneligibleToDeleteNew = values.ineligibles[index].recordId === undefined;
    setDeleteModal({
      isOpen: true,
      isNew: isIneligibleToDeleteNew,
      deleteRecordId: isIneligibleToDeleteNew ? '' : values.ineligibles[index].recordId,
      deleteName: values.ineligibles[index].description,
      deleteIndex: index,
    });
  };

  const handleConfirmDelete = (formikVals: IIneligible[], remove: (index: number) => void) => {
    if (deleteModal.isNew) {
      remove(deleteModal.deleteIndex);
      const itemName = deleteModal.deleteName ? deleteModal.deleteName : 'Item';
      setToasterMessage(`${itemName} has been deleted`);
      setToasterSeverity('success');
      setIsToasterOpen(true);
    }

    const { hasUnsavedChanges, presentFormikValues } = getPresentFormikValues(formikVals);
    if (hasUnsavedChanges && deleteModal.isNew) { setFormikValues({ ineligibles: presentFormikValues }); return; }
    if (hasUnsavedChanges && !deleteModal.isNew) { deleteIneligible(deleteModal.deleteRecordId); setFormikValues({ ineligibles: presentFormikValues }); return; }
    if (!hasUnsavedChanges && !deleteModal.isNew) { deleteIneligible(deleteModal.deleteRecordId, true); setFormikValues({ ineligibles: presentFormikValues }); return; }
    setIneligibles(presentFormikValues);
  };

  const handleSave = async (ineligiblesToSave: IIneligible[], setSubmitting: (isSubmitting: boolean) => void) => {
    const loadedPermissions = permissions as IIneligibleSettingsPermissions;
    if (!loadedPermissions.canAddAdjustments && !loadedPermissions.canUpdateAdjustments) { setSubmitting(false); return; }

    setSubmitting(true);
    const ineligiblesToEdit = ineligiblesToSave
      .filter(ineligible => ineligible.hasOwnProperty('recordId'))
      .filter(ineligible => {
        const [originalIneligible] = ineligibles.filter(currentIneligible => currentIneligible.recordId === ineligible.recordId);
        if (originalIneligible === undefined) { return false; }
        return !isObjectsEqual(ineligible, originalIneligible);
      });

    const currentBbPeriodFk = (selectedAdjustmentsAsOfDate as IBBPeriod).recordId;

    const editRequestPutConfigs = ineligiblesToEdit.map(ineligible => ({
      url: `${API_DOMAIN}/bb/ineligibleAdjustments/${ineligible.recordId}`,
      method: PUT,
      data: {
        recordId: parseInt(ineligible.recordId),
        borrowerFk: (selectedClient as IClient).recordId,
        bbPeriodFk: ineligible.bbPeriodFk,
        ...getCollateralFk((selectedCollateral as ICollateral).recordId, ineligibleType),
        description: ineligible.description,
        amount: parseFloat(ineligible.ineligibleAmount),
        appCreatedDate: dayjs(ineligible.dateCreated, 'MM/DD/YYYY').format('DD/MM/YY'),
        ineligibleType,
        isCurrent: false,
      },
    }));

    const editRequestPostConfigs = ineligiblesToEdit.map(ineligible => ({
      url: `${API_DOMAIN}/bb/ineligibleAdjustments`,
      method: POST,
      data: {
        borrowerFk: (selectedClient as IClient).recordId,
        bbPeriodFk: currentBbPeriodFk,
        ...getCollateralFk((selectedCollateral as ICollateral).recordId, ineligibleType),
        description: ineligible.description,
        amount: parseFloat(ineligible.ineligibleAmount),
        appCreatedDate: dayjs(ineligible.dateCreated, 'MM/DD/YYYY').format('DD/MM/YY'),
        ineligibleType,
        isCurrent: true,
      },
    }));

    const postRequestConfigs = ineligiblesToSave
      .filter(ineligible => !ineligible.hasOwnProperty('recordId'))
      .map(ineligible => ({
        url: `${API_DOMAIN}/bb/ineligibleAdjustments`,
        method: POST,
        data: {
          borrowerFk: (selectedClient as IClient).recordId,
          bbPeriodFk: currentBbPeriodFk,
          ...getCollateralFk((selectedCollateral as ICollateral).recordId, ineligibleType),
          description: ineligible.description,
          amount: parseFloat(ineligible.ineligibleAmount),
          appCreatedDate: dayjs(ineligible.dateCreated, 'MM/DD/YYYY').format('DD/MM/YY'),
          ineligibleType,
          isCurrent: true,
        },
      }));

    const requestsConfigs = [
      ...(loadedPermissions.canUpdateAdjustments ? editRequestPutConfigs : []),
      ...(loadedPermissions.canUpdateAdjustments ? editRequestPostConfigs : []),
      ...(loadedPermissions.canAddAdjustments ? postRequestConfigs : []),
    ];
    const updatedToasterMessage = getUpdateToasterMessage(
      loadedPermissions.canAddAdjustments ? postRequestConfigs : [],
      loadedPermissions.canUpdateAdjustments ? editRequestPostConfigs : []
    );

    requests(requestsConfigs.map(requestConfig => request(requestConfig)))
      .then(() => {
        setIsDirty(false);
        fetchIneligibles();
        setToasterMessage(updatedToasterMessage);
        setToasterSeverity('success');
        setIsToasterOpen(true);
        setSubmitting(false);
      })
      .catch((error) => {
        console.log('ineligible adjustments requests error', error);
        setSubmitting(false);
      });
  };

  const closeModal = () => setIsModalOpen(false);

  const onArchiveClick = (values: IIneligible[], index: number) => {
    setSelectedIneligible(values[index]);
    setModalTitle(`Archive ${values[index].description}`);
    setModalDescription('Are you sure you want to archive this item?');
    setModalConfirmText('Archive');
    setIsModalOpen(true);
  };

  const onRestoreClick = (values: IIneligible[], index: number) => {
    setSelectedIneligible(values[index]);
    setModalTitle(`Restore ${values[index].description}`);
    setModalDescription('Are you sure you want to restore this item?');
    setModalConfirmText('Restore');
    setIsModalOpen(true);
  };

  const handleConfirm = () => modalConfirmText === 'Archive' ? archiveIneligible() : restoreIneligible();

  const getDisplayedError = (errors: FormikErrors<IFormikValue>, touched: FormikTouched<IFormikValue>, name: string, index: number) => {
    const fieldError = getIn(errors, `ineligibles[${index}].${name}`);
    const isFieldTouched = getIn(touched, `ineligibles[${index}].${name}`);
    if (fieldError && isFieldTouched) { return { error: true, helperText: <HelperTextComponent text={fieldError} /> }; }
    return null;
  };

  const addNewRow = (push: (obj: unknown) => void) => {
    push({ dateCreated: dayjs().format('MM/DD/YYYY'), description: '', ineligibleAmount: '' });
    setAllowAddingRow(false);
  };

  const getUpdateToasterMessage = (addItems: { data: { description: string } }[], updateItems: { data: { description: string } }[]) => {
    const addLength = addItems.length;
    const updateLength = updateItems.length;
    if (addLength > 0 && updateLength > 0) { return 'Changes in ineligible adjustments have been saved'; }
    if (addLength > 0) {
      const phrase = addLength > 1 ? 'Items have been' : `${addItems[0].data.description} has been`;
      return `${phrase} added`;
    }
    const phrase = updateLength > 1 ? 'Items have been' : `${updateItems[0]?.data.description ?? 'Item'} has been`;
    return `${phrase} updated`;
  };

  const getPresentFormikValues = (formikValuesForIneligibles: IIneligible[]) => {
    let hasUnsavedChanges = false;
    const presentFormikValues = formikValuesForIneligibles.filter((currentValue, currentIndex) => {
      if (currentIndex === deleteModal.deleteIndex) { return false; }
      if (currentValue.recordId === deleteModal.deleteRecordId) { return false; }

      const hasNewRecord = currentValue.recordId === undefined;
      const hasUpdatedRecord = ineligibles.some(ineligible => {
        if (ineligible.recordId !== currentValue.recordId) { return false; }
        return !isObjectsEqual(currentValue, ineligible);
      });
      if (hasNewRecord || hasUpdatedRecord) { hasUnsavedChanges = true; }
      return true;
    });
    return { hasUnsavedChanges, presentFormikValues };
  };

  const getSavedAndEmptyFormikValues = (formikValuesForIneligibles: IIneligible[]) => {
    const [savedFormikValues, emptyFormikValues] = formikValuesForIneligibles.reduce((separatedFormikValues: IIneligible[][], ineligible) => {
      const currentSavedFormikValues = [...separatedFormikValues[0]];
      const currentEmptyFormikValues = [...separatedFormikValues[1]];

      const isIneligibleSaved = ineligible.recordId !== undefined && ineligible.recordId !== '';
      if (isIneligibleSaved) {
        const [savedIneligible] = ineligibles.filter(originalIneligible => originalIneligible.recordId === ineligible.recordId);
        savedIneligible !== undefined && currentSavedFormikValues.push(savedIneligible);
        return [currentSavedFormikValues, currentEmptyFormikValues];
      }

      const isIneligibleEmpty = (ineligible.description === undefined || ineligible.description === '')
        && (ineligible.ineligibleAmount === undefined || ineligible.ineligibleAmount === '');
      if (isIneligibleEmpty) { currentEmptyFormikValues.push(ineligible); }
      return [currentSavedFormikValues, currentEmptyFormikValues];
    }, [[], []]);

    return { savedFormikValues, emptyFormikValues };
  };

  const hasArchivedItems = () => selectedTabIndex !== TABS.findIndex(tab => tab.label === 'Archived') || ineligibles.length > 0;

  const syncFormikRef = (node: FormikProps<IFormikValue> | null) => {
    props.formikRef.current = node;
    if (node === null || isLoading) { return; }
    setFormikValues(node.values);
    !isObjectsEqual(formikValues, node.values) && setIsDirty(node.dirty);
    setIsAdjustmentsDirty(node.dirty);
    if (!isFormikResetting) { return; }
    node.resetForm();
    setIsFormikResetting(false);
  };

  const getAction = (ineligible: IIneligible, formik: FormikProps<IFormikValue>, index: number) => {
    const loadedPermissions = permissions as IIneligibleSettingsPermissions;
    if (!loadedPermissions.canDeleteArchiveAdjustments) { return null; }
    if (selectedTabIndex === TABS.findIndex(tab => tab.label === 'Active')) {
      if (ineligible.canDelete !== false) {
        return (
          <Tooltip title='Delete the item'>
            <IconButton onClick={() => handleDelete(formik.values, index)} sx={styles.actionButton} color='primary' aria-label='Delete icon'>
              <DeleteOutlined />
            </IconButton>
          </Tooltip>
        );
      }
      return (
        <Tooltip title='Archive the item'>
          <IconButton onClick={() => onArchiveClick(formik.values.ineligibles, index)} sx={styles.actionButton} color='primary' aria-label='Archive icon'>
            <Inventory2OutlinedIcon />
          </IconButton>
        </Tooltip>
      );
    }
    return (
      <Tooltip title='Restore the item'>
        <IconButton onClick={() => onRestoreClick(formik.values.ineligibles, index)} sx={styles.actionButton} color='primary' aria-label='Restore icon'>
          <HistoryIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const isFieldDisabled = (index: number) => {
    const loadedPermissions = permissions as IIneligibleSettingsPermissions;
    if (selectedTabIndex === TABS.findIndex(tab => tab.label === 'Archived')) { return true; }
    return formikValues?.ineligibles[index]?.recordId === undefined ? !loadedPermissions.canAddAdjustments : !loadedPermissions.canUpdateAdjustments;
  };

  const getFieldTabIndex = (index: number) => isFieldDisabled(index) ? 0 : null;

  const getRowAdder = (formik: FormikProps<IFormikValue>, push: (obj: unknown) => void) => (
    <TableRow sx={styles.tableRow} key={formik.values.ineligibles.length} data-testid='row-adder'>
      {HEADERS.map((header, index) => (
        <TableCell sx={styles.tableCell} key={header}>
          {index < HEADERS.length - 1 && (
            <TextField
              sx={styles.textField}
              value=''
              onClick={() => addNewRow(push)}
              onKeyDown={(e) => { if (e.key === 'Enter') { addNewRow(push); } }}
              inputProps={{ sx: { ...(header === 'Date Created' && styles.centerAlignedText) }, 'aria-label': 'Press enter to add new entry' }}
              placeholder={header === 'Date Created' ? `${formatDate('', AMERICAN_DATE_FORMAT)}` : ''}
              disabled={header === 'Date Created'}
              tabIndex={header === 'Date Created' ? 0 : 1}
            />
          )}
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <Box sx={styles.outerBox}>
      { selectedAdjustmentsAsOfDate === null ? (
        <Box sx={styles.emptyStateBox}>
          <Typography tabIndex={0} sx={styles.emptyStateText}>Select an As of Date to view or create ineligible adjustments.</Typography>
        </Box>
      ) : (
        <Formik
          enableReinitialize
          innerRef={syncFormikRef}
          initialValues={{ ineligibles } as IFormikValue}
          validationSchema={ineligibleAdjustmentsSchema}
          onSubmit={(values, { setSubmitting }) => handleSave(values.ineligibles, setSubmitting)}
        >
          {(formik) => (
            <Form>
              <Box sx={styles.totalAmountContainer}>
                <Typography tabIndex={0} sx={styles.totalAmountLabel}>Total Amount:</Typography>
                <Typography tabIndex={0} sx={styles.totalAmountValue}>
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalAmount)}
                </Typography>
              </Box>
              <Tabs aria-label='ineligible adjustments status tab' value={selectedTabIndex} onChange={handleTabChange} sx={styles.buttonTabs}>
                {TABS.map((tab, index) => <Tab tabIndex={0} label={tab.label} key={tab.label} {...tabProps(index)} />)}
              </Tabs>
              {(!hasArchivedItems() && !isLoading) && (
                <Typography tabIndex={0} sx={styles.emptyStateText}>There are no archives. Please add an archive to start.</Typography>
              )}
              {isLoading && (
                <Table data-testid='ineligible-adjustment-loader' sx={styles.table}>
                  <SkeletonTableHead />
                  <TableBody>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                  </TableBody>
                </Table>
              )}
              <TableContainer sx={styles.tableContainer} hidden={!hasArchivedItems() || isLoading}>
                <Table sx={styles.table}>
                  <TableHead>
                    <TableRow sx={styles.tableHeadRow}>
                      <TableCell sx={{ ...styles.tableHeadCell, ...styles.tableHeadCellForDateCreated, ...styles.centerAlignedText }}>
                        <FormLabel tabIndex={0} htmlFor='date-created-box' sx={styles.tableHeaderText}>Date Created</FormLabel>
                      </TableCell>
                      <TableCell sx={styles.tableHeadCell}>
                        <FormLabel tabIndex={0} htmlFor='description-box' sx={{ ...styles.tableHeaderText, ...styles.tableHeadCellForDescription }}>
                          Description<span style={styles.asterisk}> *</span>
                        </FormLabel>
                      </TableCell>
                      <TableCell sx={{ ...styles.tableHeadCell, ...styles.tableHeadCellForIneligibleAmount, ...styles.rightAlignedText }}>
                        <FormLabel tabIndex={0} htmlFor='ineligible-amount-box' sx={styles.tableHeaderText}>
                          Ineligible Amount<span style={styles.asterisk}> *</span>
                        </FormLabel>
                      </TableCell>
                      <TableCell sx={{ ...styles.tableHeadCell, ...styles.tableHeadCellForAction, ...styles.centerAlignedText }}>
                        <FormLabel tabIndex={0} sx={styles.tableHeaderText}>Action</FormLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <FieldArray name='ineligibles'>
                    {({ push, remove }) => (
                      <TableBody sx={styles.tableBody}>
                        {formik.values.ineligibles.map((ineligible, index) => (
                          <TableRow sx={styles.tableRow} key={ineligible.recordId} aria-label={`${ineligible.description}`}>
                            <TableCell sx={styles.tableCell}>
                              <Field
                                tabIndex={0}
                                id='date-created-box'
                                aria-label={`${formik.values.ineligibles[index].dateCreated}`}
                                name={`ineligibles[${index}].dateCreated`}
                                as={TextField}
                                sx={styles.textField}
                                disabled
                                variant='outlined'
                                inputProps={{ sx: styles.centerAlignedText, 'aria-label': `${formik.values.ineligibles[index].dateCreated}` }}
                                {...getDisplayedError(formik.errors, formik.touched, 'dateCreated', index)}
                              />
                            </TableCell>
                            <TableCell sx={styles.tableCell}>
                              <Field
                                disabled={isFieldDisabled(index)}
                                tabIndex={getFieldTabIndex(index)}
                                id='description-box'
                                aria-label='Description Field'
                                name={`ineligibles[${index}].description`}
                                as={TextField}
                                sx={styles.textField}
                                variant='outlined'
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => { formik.handleChange(event); }}
                                inputProps={{ 'aria-label': 'Description' }}
                                {...getDisplayedError(formik.errors, formik.touched, 'description', index)}
                                onBlur={(event: React.FocusEvent<HTMLInputElement>) => trimOnBlur(event, formik, `ineligibles[${index}].description`, formik.values.ineligibles[index].description)}
                              />
                            </TableCell>
                            <TableCell sx={styles.tableCell}>
                              <Field
                                disabled={isFieldDisabled(index)}
                                tabIndex={getFieldTabIndex(index)}
                                id='ineligible-amount-box'
                                aria-label='Ineligible Amount Field'
                                name={`ineligibles[${index}].ineligibleAmount`}
                                as={TextField}
                                sx={styles.textField}
                                variant='outlined'
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => { formik.handleChange(event); }}
                                onBlur={(event: React.FocusEvent<HTMLInputElement>) => formik.handleBlur(event)}
                                InputProps={{ inputComponent: CurrencyFormat as never }}
                                inputProps={{ sx: styles.rightAlignedText, 'aria-label': 'Ineligible Amount' }}
                                {...getDisplayedError(formik.errors, formik.touched, 'ineligibleAmount', index)}
                              />
                            </TableCell>
                            <TableCell align='center' sx={styles.actionTableCell}>
                              {getAction(ineligible, formik, index)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {(allowAddingRow && selectedTabIndex === TABS.findIndex(tab => tab.label === 'Active') && (permissions as IIneligibleSettingsPermissions).canAddAdjustments) &&
                          getRowAdder(formik, push)}
                        <ConfirmModal
                          open={deleteModal.isOpen}
                          onClose={() => setDeleteModal({ ...deleteModal, isOpen: false })}
                          onConfirm={() => handleConfirmDelete(formik.values.ineligibles, remove)}
                          title={`Delete ${deleteModal.deleteName}`}
                          description='Are you sure you want to delete this item?'
                          errorButton
                          yesButtonText='Delete'
                          noButtonText='Cancel'
                        />
                      </TableBody>
                    )}
                  </FieldArray>
                </Table>
              </TableContainer>
            </Form>
          )}
        </Formik>
      )}
      <Toaster open={isToasterOpen} message={toasterMessage} severity={toasterSeverity} onCloseChange={() => setIsToasterOpen(false)} />
      <ConfirmModal
        open={showPrompt}
        onConfirm={() => setShowPrompt(false)}
        onClose={handleDiscardTabChange}
        onButtonClose={() => setShowPrompt(false)}
        promptChecker
        title='Confirm Navigation'
        description='You have unsaved changes. Are you sure you want to leave this page?'
        yesButtonText='Keep Editing'
        noButtonText='Discard Changes'
        confirmOnly
      />
      <ConfirmModal
        title={modalTitle}
        description={modalDescription}
        open={isModalOpen}
        alignment='left'
        onClose={closeModal}
        onConfirm={handleConfirm}
        noButtonText='Cancel'
        yesButtonText={modalConfirmText}
      />
    </Box>
  );
};

export default IneligibleAdjustmentsPanel;
